import { useRef, useState, useEffect } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { client, Address } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { SearchInput } from '../Table';
import Button from 'design-system/src/components/Button/Button';
import LocationPin from 'design-system/src/assets/icons/locationPin.svg?react';
import { formatAddress } from '../../utils';

const PharmacySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SearchContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${({ $fullWidth }) => ($fullWidth ? 'width: 100%;' : 'margin-right: auto;')}
`;

const SectionHeader = styled.div`
  background-color: var(--surface-200);
  padding: 12px;
  border-radius: var(--border-radius-medium);
  margin-bottom: 4px;
`;

const SelectedPharmacyCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--grayscale-200);
  border-radius: var(--border-radius-medium);
`;

const PharmacyIconWrapper = styled.div`
  color: var(--purple-700);
  flex-shrink: 0;
`;

const PharmacyDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RemoveButtonWrapper = styled.div`
  flex-shrink: 0;
`;

const libraries = ['places'];

type PharmacyAddress = {
  name: string;
  address: Address;
  formatted_address: string;
  phone_number: string;
  google_place_id: string;
  latitude: number;
  longitude: number;
};

type PharmacySearchProps = {
  rxId: string;
  onSubmit: () => void;
  onSelectionChange?: (hasSelection: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  submitTrigger?: number; // Increment to trigger submit from parent
  hideHeaders?: boolean; // Hide the section headers (useful when used inside a modal with its own title)
};

const PharmacySearch = ({
  rxId,
  onSubmit,
  onSelectionChange,
  onSubmittingChange,
  submitTrigger,
  hideHeaders = false,
}: PharmacySearchProps) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const placesDivRef = useRef<HTMLDivElement | null>(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyAddress | null>(null);

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_CLIENT_KEY || '';

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as any,
  });

  const extractAddress = (addressComponents: any[]): Address => {
    const getComponent = (type: string) => {
      const comp = addressComponents.find((c: any) => c.types.includes(type));
      return comp ? comp.long_name : '';
    };
    return {
      line1: getComponent('street_number')
        ? `${getComponent('street_number')} ${getComponent('route')}`.trim()
        : getComponent('route'),
      line2: '',
      city: getComponent('locality') || getComponent('sublocality') || getComponent('postal_town'),
      state: getComponent('administrative_area_level_1'),
      postal_code: getComponent('postal_code'),
    };
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (place && place.place_id) {
      const { maps } = (window as any).google;
      const service = new maps.places.PlacesService(placesDivRef.current || document.createElement('div'));
      service.getDetails(
        {
          placeId: place.place_id,
          fields: ['name', 'formatted_address', 'address_components', 'formatted_phone_number', 'place_id', 'geometry'],
        },
        (details: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && details && details.address_components) {
            const address = extractAddress(details.address_components || []);
            setSelectedPharmacy({
              name: details.name || '',
              address,
              formatted_address: details.formatted_address || '',
              phone_number: details.formatted_phone_number || '',
              google_place_id: details.place_id || '',
              latitude: details.geometry?.location?.lat() || 0,
              longitude: details.geometry?.location?.lng() || 0,
            });
          }
        },
      );
    }
  };

  const updatePharmacyMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPharmacy) {
        Sentry.captureException('No pharmacy selected');
        return;
      }
      const payload = {
        name: selectedPharmacy.name,
        address_line_1: selectedPharmacy.address.line1,
        formatted_address: selectedPharmacy.formatted_address,
        city: selectedPharmacy.address.city,
        state: selectedPharmacy.address.state,
        zip_code: selectedPharmacy.address.postal_code,
        phone_number: selectedPharmacy.phone_number,
        google_place_id: selectedPharmacy.google_place_id,
        latitude: selectedPharmacy.latitude,
        longitude: selectedPharmacy.longitude,
      };
      return client.post(`/rxs/${rxId}/preferred-pharmacy`, payload).then((res) => res.data);
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Pharmacy updated</Toast>);
      onSubmit();
    },
    onError: () => {
      toast.custom(() => <Toast variant="error">Failed to update pharmacy</Toast>);
    },
  });

  // Notify parent when selection changes
  useEffect(() => {
    onSelectionChange?.(selectedPharmacy !== null);
  }, [selectedPharmacy, onSelectionChange]);

  // Notify parent when submitting state changes
  useEffect(() => {
    onSubmittingChange?.(updatePharmacyMutation.isPending);
  }, [updatePharmacyMutation.isPending, onSubmittingChange]);

  // Handle submit trigger from parent
  useEffect(() => {
    if (submitTrigger && submitTrigger > 0 && selectedPharmacy) {
      updatePharmacyMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitTrigger]);

  return (
    <PharmacySearchContainer>
      {!hideHeaders && (
        <SectionHeader>
          <Typography styledAs="bodyDMSans" weight={600}>
            Select patient's preferred pharmacy
          </Typography>
        </SectionHeader>
      )}

      {!selectedPharmacy && (
        <>
          {!hideHeaders && (
            <Typography styledAs="bodySmallDMSans" weight={600}>
              Pharmacy lookup
            </Typography>
          )}

          {isLoaded && (
            <Autocomplete
              onLoad={(loadedRef) => {
                autocompleteRef.current = loadedRef;
              }}
              onPlaceChanged={handlePlaceChanged}
              options={{
                types: ['establishment'],
                componentRestrictions: { country: 'us' },
                fields: ['name', 'formatted_address', 'geometry', 'place_id'],
              }}
            >
              <SearchContainer $fullWidth={!hideHeaders}>
                <SearchInput
                  label="Search for a pharmacy"
                  placeholder="Search by pharmacy name, address, or NPI"
                  $width="100%"
                  hideLabel={true}
                />
              </SearchContainer>
            </Autocomplete>
          )}
        </>
      )}

      {/* This is a hidden div that is used to store the places service */}
      <div ref={placesDivRef} style={{ display: 'none' }} />

      {selectedPharmacy && (
        <SelectedPharmacyCard>
          <PharmacyIconWrapper>
            <LocationPin width={24} height={24} aria-hidden />
          </PharmacyIconWrapper>
          <PharmacyDetails>
            {selectedPharmacy.name && (
              <Typography styledAs="bodyDMSans" weight={600}>
                {selectedPharmacy.name}
              </Typography>
            )}
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-700)">
              {formatAddress(
                selectedPharmacy.address.line1,
                selectedPharmacy.address.city,
                selectedPharmacy.address.state,
                selectedPharmacy.address.postal_code,
              )}
            </Typography>
            {selectedPharmacy.phone_number && (
              <Typography styledAs="bodySmallDMSans" color="var(--grayscale-700)">
                {selectedPharmacy.phone_number}
              </Typography>
            )}
          </PharmacyDetails>
          <RemoveButtonWrapper>
            <Button variant="secondary" size="sm" onClick={() => setSelectedPharmacy(null)}>
              Remove
            </Button>
          </RemoveButtonWrapper>
        </SelectedPharmacyCard>
      )}
    </PharmacySearchContainer>
  );
};

export default PharmacySearch;
